import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { User, Plus, Trash2, Edit, FileText, Globe, KeyRound, Network, Briefcase, LayoutList, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { useCounters, meterPropsFromTenant } from "@/lib/counters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { ChannelMeter, EmptyState, PageHeader, PolicyPill, StatusPill } from "@/components/uiext";
import { TenantInlineEditor, useTenants, StatusSquare } from "./_tenantCommon.jsx";
import { TracesSheet } from "@/components/TracesSheet";
import { useConfirm } from "@/lib/confirm.jsx";
import { toast } from "sonner";

export default function CustomersPage() {
  const { t } = useTranslation();
  const { items, reload } = useTenants("customer");
  // Phase 6o Hotfix H — reseller hierarchy retired.
  // Phase 6o Hotfix I — business_type filter + group-by + chip row.
  //   - selectedCategory: "" = all (default), "__none__" = uncategorised,
  //     anything else = exact business_type match
  //   - groupByCategory: false by default; toggle in toolbar.
  const [editor, setEditor] = useState(null);
  const [filter, setFilter] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [bizSummary, setBizSummary] = useState([]); // [{value, count}]
  const counters = useCounters(items.length);
  const [tracesParty, setTracesParty] = useState(null);
  const confirm = useConfirm();

  // Phase 6o Hotfix I — refresh chip-row counts whenever the tenant
  // list changes (after save/delete) so the chips match reality.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/tenants/business-types")
      .then((r) => {
        if (!cancelled) setBizSummary(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!cancelled) setBizSummary([]);
      });
    return () => {
      cancelled = true;
    };
  }, [items.length]);

  const del = async (tn) => {
    const ok = await confirm({
      title: t("confirm.deleteCustomer", { name: tn.name }),
      description:
        "All SIP registrations, auth credentials and CDRs for this customer will be removed. This cannot be undone.",
      confirmLabel: t("common.delete") || "Delete",
      cancelLabel: t("common.cancel") || "Cancel",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/tenants/${tn.id}`);
      toast.success(t("common.deleted"));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    }
  };

  // Phase 6o Hotfix H — resellerName() helper fully retired.
  // Phase 6o Hotfix I — filter pipeline: free-text search AND
  // selected category. Category "" = all; "__none__" = uncategorised
  // only.
  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    return items.filter((tn) => {
      if (q) {
        const matches =
          tn.name.toLowerCase().includes(q) ||
          (tn.domain || "").toLowerCase().includes(q) ||
          (tn.business_type || "").toLowerCase().includes(q) ||
          (tn.auth?.username || "").toLowerCase().includes(q);
        if (!matches) return false;
      }
      if (selectedCategory === "__none__") {
        return !tn.business_type;
      }
      if (selectedCategory) {
        return (tn.business_type || "").toLowerCase() === selectedCategory.toLowerCase();
      }
      return true;
    });
  }, [items, filter, selectedCategory]);

  // Phase 6o Hotfix I — group rows by business_type when toggle on.
  // Returns an ordered list [{label, value, rows}]. Counts in section
  // headers are post-filter, so the operator sees what's currently
  // visible, not the global org count.
  const grouped = useMemo(() => {
    if (!groupByCategory) return null;
    const map = new Map();
    for (const tn of filtered) {
      const k = tn.business_type || "__none__";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(tn);
    }
    const sortedKeys = Array.from(map.keys()).sort((a, b) => {
      // Uncategorised always last; everything else by descending size
      // then alphabetic.
      if (a === "__none__") return 1;
      if (b === "__none__") return -1;
      const da = map.get(a).length;
      const dbn = map.get(b).length;
      if (dbn !== da) return dbn - da;
      return a.localeCompare(b);
    });
    return sortedKeys.map((k) => ({
      value: k,
      label:
        k === "__none__"
          ? t("pages.customers.uncategorised", "Uncategorised")
          : k,
      rows: map.get(k),
    }));
  }, [filtered, groupByCategory, t]);

  // Phase 6n — Master-detail layout.
  // When the editor is open on lg+ viewports, the customer list stays
  // visible on the left and the editor lives on the right of a
  // ResizablePanelGroup (3CX/Vodia pattern). Switching between
  // customers becomes a single click — no back navigation required.
  // On smaller viewports the editor still takes over the full page,
  // because fitting a tabbed editor + list into a narrow column would
  // wreck legibility.
  const editing = editor !== null;
  const editorTenant = editor === "new" ? null : editor;
  const editorPane = editing ? (
    <TenantInlineEditor
      tenant={editorTenant}
      onCancel={() => setEditor(null)}
      onSaved={() => {
        setEditor(null);
        reload();
      }}
    />
  ) : null;

  // Small-screen fallback: editor fills the page.
  if (editing) {
    return (
      <>
        <div className="lg:hidden">{editorPane}</div>
        <div className="hidden lg:block" data-testid="customers-master-detail">
          <ResizablePanelGroup
            direction="horizontal"
            className="min-h-[calc(100vh-var(--toolbar-h)-8px)] rounded-lg border border-border bg-card overflow-hidden"
          >
            <ResizablePanel defaultSize={34} minSize={24} maxSize={50}>
              <div className="h-full overflow-y-auto p-3">
                <CustomersListPane
                  t={t}
                  filter={filter}
                  setFilter={setFilter}
                  filtered={filtered}
                  items={items}
                  counters={counters}
                  setEditor={setEditor}
                  setTracesParty={setTracesParty}
                  del={del}
                  editor={editor}
                  compact
                />
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={66} minSize={50}>
              <div className="h-full overflow-y-auto">
                {editorPane}
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </>
    );
  }

  return (
    <div data-testid="page-customers">
      <PageHeader
        title={t("pages.customers.title")}
        subtitle={t("pages.customers.subtitle")}
        icon={User}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder={t("pages.customers.searchPlaceholder")}
              className="w-full sm:w-[260px]"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="customers-search"
            />
            {/* Phase 6o Hotfix I — Group-by-category toggle. */}
            <label
              className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap cursor-pointer"
              data-testid="customers-groupby-toggle"
              title={t("pages.customers.groupByTitle", "Group rows by business type")}
            >
              <Switch
                checked={groupByCategory}
                onCheckedChange={setGroupByCategory}
                aria-label={t("pages.customers.groupByLabel", "Group by category")}
                className="scale-90"
              />
              {groupByCategory ? (
                <Layers className="h-3.5 w-3.5" />
              ) : (
                <LayoutList className="h-3.5 w-3.5" />
              )}
              <span>{t("pages.customers.groupByLabel", "Group by category")}</span>
            </label>
            <Button
              onClick={() => setEditor("new")}
              data-testid="new-customer-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> {t("pages.customers.newBtn")}
            </Button>
          </div>
        }
      />
      {/* Phase 6o Hotfix I — Business-type chip row. One chip per
          distinct value with its count, plus a leading "All" chip and
          a trailing "Uncategorised" chip when the org has any. The
          active chip is filled with the accent colour. Clicking a chip
          toggles its filter; clicking the active one returns to "All". */}
      {bizSummary.length > 0 && (
        <div
          className="mb-3 mt-1 flex flex-wrap items-center gap-1.5 px-1"
          data-testid="customers-chip-row"
        >
          <Briefcase className="h-3.5 w-3.5 text-muted-foreground mr-0.5" />
          <ChipBtn
            label={t("pages.customers.chipAll", "All")}
            count={items.length}
            active={!selectedCategory}
            onClick={() => setSelectedCategory("")}
            testid="chip-all"
          />
          {bizSummary
            .filter((r) => r.value)
            .map((r) => (
              <ChipBtn
                key={r.value}
                label={r.value}
                count={r.count}
                active={selectedCategory.toLowerCase() === r.value.toLowerCase()}
                onClick={() =>
                  setSelectedCategory(
                    selectedCategory.toLowerCase() === r.value.toLowerCase()
                      ? ""
                      : r.value
                  )
                }
                testid={`chip-${r.value}`}
              />
            ))}
          {bizSummary.find((r) => !r.value && r.count > 0) && (
            <ChipBtn
              label={t("pages.customers.uncategorised", "Uncategorised")}
              count={bizSummary.find((r) => !r.value)?.count || 0}
              active={selectedCategory === "__none__"}
              onClick={() =>
                setSelectedCategory(selectedCategory === "__none__" ? "" : "__none__")
              }
              testid="chip-uncategorised"
              muted
            />
          )}
        </div>
      )}
      {filtered.length === 0 ? (
        <EmptyState
          title={
            items.length ? t("pages.customers.noMatchesTitle") : t("pages.customers.emptyTitle")
          }
          description={
            items.length
              ? t("pages.customers.noMatchesDesc")
              : t("pages.customers.emptyDesc")
          }
          action={
            items.length ? null : (
              <Button onClick={() => setEditor("new")}>
                <Plus className="h-4 w-4 mr-1" /> {t("pages.customers.emptyAction")}
              </Button>
            )
          }
        />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader className="sticky-table-head">
              <TableRow>
                <TableHead>{t("fields.name")}</TableHead>
                <TableHead>{t("fields.domain")}</TableHead>
                <TableHead>{t("pages.customers.colAuth")}</TableHead>
                <TableHead className="w-[220px]">{t("fields.channels")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead className="w-[120px] text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                // Phase 6o Hotfix I — render either flat rows or
                // per-category groups with section-header rows when
                // the "Group by category" toggle is on.
                const renderRow = (tn) => (
                  <TableRow
                    key={tn.id}
                    className="row-hover cursor-pointer"
                    data-testid={`customer-row-${tn.id}`}
                    onClick={(e) => {
                      if (e.target.closest("button")) return;
                      setEditor(tn);
                    }}
                  >
                    <TableCell>
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        {tn.name}
                        <PolicyPill entity={tn} compact />
                      </div>
                      <div className="text-xs text-muted-foreground font-mono flex items-center gap-1.5">
                        <span>{tn.id.slice(0, 12)}</span>
                        {tn.business_type ? (
                          <>
                            <span className="opacity-50">·</span>
                            <span className="inline-flex items-center gap-1 normal-case">
                              <Briefcase className="h-3 w-3" />
                              {tn.business_type}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </TableCell>
                    {/* Phase 6o Hotfix H — Reseller column removed */}
                    <TableCell>
                      <span className="inline-flex items-center gap-1 font-mono text-xs">
                        <Globe className="h-3 w-3 text-muted-foreground" /> {tn.domain || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusPill kind={tn.auth?.mode === "ip" ? "info" : "ok"}>
                        {tn.auth?.mode === "ip" ? (
                          <>
                            <Network className="h-3 w-3 mr-0.5" /> IP
                          </>
                        ) : (
                          <>
                            <KeyRound className="h-3 w-3 mr-0.5" /> Digest
                          </>
                        )}
                      </StatusPill>
                    </TableCell>
                    <TableCell>
                      <ChannelMeter {...meterPropsFromTenant(tn, counters, "customer")} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <StatusSquare
                          kind={tn.enabled ? "ok" : "offline"}
                          title={tn.enabled ? t("common.enabled") : t("common.disabled")}
                        />
                        <span className="text-xs">
                          {tn.enabled ? t("common.enabled") : t("common.disabled")}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        title={t("pages.trunks.tracesTitle")}
                        onClick={() => setTracesParty({ id: tn.id, name: tn.name, kind: "customer" })}
                        data-testid={`customer-traces-${tn.id}`}
                      >
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditor(tn)}
                      data-testid={`customer-edit-${tn.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-[hsl(var(--danger))]"
                      onClick={() => del(tn)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
                );
                if (groupByCategory && grouped) {
                  // Render a section-header row before each group, then
                  // its rows. colSpan=6 covers all columns we render.
                  return grouped.flatMap((g) => [
                    <TableRow
                      key={`group-${g.value}`}
                      className="bg-muted/30 hover:bg-muted/30"
                      data-testid={`group-header-${g.value}`}
                    >
                      <TableCell colSpan={6} className="py-1.5">
                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
                          <Briefcase className="h-3 w-3" />
                          <span className="font-semibold text-foreground">{g.label}</span>
                          <span className="opacity-60">({g.rows.length})</span>
                        </div>
                      </TableCell>
                    </TableRow>,
                    ...g.rows.map(renderRow),
                  ]);
                }
                return filtered.map(renderRow);
              })()}
            </TableBody>
          </Table>
        </div>
      )}
      <TracesSheet
        open={!!tracesParty}
        onOpenChange={(v) => !v && setTracesParty(null)}
        party={tracesParty}
      />
    </div>
  );
}

/**
 * CustomersListPane — compact version of the customer list used in the
 * master-detail left pane on lg+ viewports. Shows name, status square,
 * auth mode badge and a pinned-row accent on the currently-selected
 * customer. Clicking a row swaps the editor on the right without ever
 * leaving the page.
 */
function CustomersListPane({
  t,
  filter,
  setFilter,
  filtered,
  items,
  counters,
  setEditor,
  setTracesParty,
  del,
  editor,
}) {
  const selectedId = editor && editor !== "new" ? editor.id : null;
  return (
    <div className="flex flex-col gap-3 h-full min-h-0">
      <div className="flex items-center gap-2">
        <Input
          placeholder={t("pages.customers.searchPlaceholder")}
          className="flex-1 h-8 text-sm"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="customers-search-compact"
        />
        <Button
          size="sm"
          className="h-8"
          onClick={() => setEditor("new")}
          data-testid="new-customer-btn-compact"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-md bg-card p-4 text-xs text-muted-foreground">
          {items.length
            ? t("pages.customers.noMatchesTitle")
            : t("pages.customers.emptyTitle")}
        </div>
      ) : (
        <ul className="flex flex-col gap-0.5 min-h-0" data-testid="customers-compact-list">
          {filtered.map((tn) => {
            const selected = tn.id === selectedId;
            return (
              <li key={tn.id}>
                <button
                  type="button"
                  onClick={() => setEditor(tn)}
                  data-testid={`customer-row-compact-${tn.id}`}
                  className={[
                    "relative w-full text-left rounded-md px-2.5 py-2 flex items-center gap-2.5 transition-colors",
                    selected
                      ? "bg-[hsl(var(--accent)/0.12)]"
                      : "hover:bg-muted/60",
                  ].join(" ")}
                >
                  {selected && (
                    <span
                      aria-hidden="true"
                      className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-[hsl(var(--accent))]"
                    />
                  )}
                  <StatusSquare
                    kind={tn.enabled ? "ok" : "offline"}
                    title={
                      tn.enabled ? t("common.enabled") : t("common.disabled")
                    }
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-1.5">
                      {tn.name}
                      {tn.auth?.mode === "ip" ? (
                        <span
                          className="text-[10px] font-mono px-1 rounded bg-muted text-muted-foreground"
                          title={t("fields.authIp")}
                        >
                          IP
                        </span>
                      ) : (
                        <span
                          className="text-[10px] font-mono px-1 rounded bg-muted text-muted-foreground"
                          title={t("fields.authDigest")}
                        >
                          DIG
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-mono truncate">
                      {tn.domain || "—"}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * ChipBtn — Phase 6o Hotfix I.
 * Tiny pill button used in the Customers chip-row.
 *   active=true  → filled with accent colour, white text
 *   muted=true   → subdued grey style for "Uncategorised"
 */
function ChipBtn({ label, count, active, onClick, testid, muted = false }) {
  const base =
    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors";
  let cls;
  if (active) {
    cls = muted
      ? "bg-muted-foreground/30 text-foreground border border-muted-foreground/40"
      : "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border border-transparent";
  } else {
    cls = muted
      ? "bg-muted/30 text-muted-foreground border border-border hover:bg-muted/50"
      : "bg-card text-foreground border border-border hover:border-[hsl(var(--accent))]/60 hover:text-[hsl(var(--accent))]";
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${cls}`}
      data-testid={testid}
    >
      <span className="truncate max-w-[160px]">{label}</span>
      <span
        className={`tabular-nums text-[10px] ${
          active ? "opacity-90" : "opacity-70"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

